import React, { useState, useCallback } from "react";
import { Terminal, Plus, Trash2, GripVertical } from "lucide-react";
import { ProcessorNodeData } from "../../types/pipeline";

interface ShellConfigHelperProps {
  onConfigGenerated: (config: any) => void;
  disabled?: boolean;
  selectedNode: { id: string; data: ProcessorNodeData } | null;
  updateNode: (
    id: string,
    updates: Partial<{ data: ProcessorNodeData }>
  ) => void;
}

interface Command {
  id: string;
  content: string;
}

/**
 * ShellConfigHelper - UI for managing shell commands (SWRM-174)
 *
 * Provides a user-friendly interface for:
 * - Adding/removing commands
 * - Editing command content with multi-line support
 * - Reordering commands via drag-and-drop
 * - Setting timeout and continue_on_error flag
 */
export const ShellConfigHelper: React.FC<ShellConfigHelperProps> = ({
  onConfigGenerated,
  disabled,
  selectedNode,
  updateNode,
}) => {
  // Initialize from node config
  const initialCommands = selectedNode?.data.config?.commands || [
    "# Add your commands here",
    "echo 'Hello World'",
  ];
  const [commands, setCommands] = useState<Command[]>(() =>
    initialCommands.map((cmd: string, idx: number) => ({
      id: `cmd-${Date.now()}-${idx}`,
      content: cmd,
    }))
  );

  const [timeout, setTimeoutValue] = useState(
    selectedNode?.data.config?.timeout || 300
  );
  const [continueOnError, setContinueOnError] = useState(
    selectedNode?.data.config?.continue_on_error || false
  );
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Generate config from current state
  const generateConfig = useCallback(() => {
    const config = {
      commands: commands.map((cmd) => cmd.content),
      timeout,
      continue_on_error: continueOnError,
    };
    onConfigGenerated(config);
  }, [commands, timeout, continueOnError, onConfigGenerated]);

  // Add new command
  const handleAddCommand = useCallback(() => {
    const newCommand: Command = {
      id: `cmd-${Date.now()}`,
      content: "",
    };
    setCommands((prev) => [...prev, newCommand]);
  }, []);

  // Remove command
  const handleRemoveCommand = useCallback((id: string) => {
    setCommands((prev) => prev.filter((cmd) => cmd.id !== id));
  }, []);

  // Update command content
  const handleCommandChange = useCallback((id: string, content: string) => {
    setCommands((prev) =>
      prev.map((cmd) => (cmd.id === id ? { ...cmd, content } : cmd))
    );
  }, []);

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === index) return;

      const newCommands = [...commands];
      const draggedCommand = newCommands[draggedIndex];
      newCommands.splice(draggedIndex, 1);
      newCommands.splice(index, 0, draggedCommand);

      setCommands(newCommands);
      setDraggedIndex(index);
    },
    [draggedIndex, commands]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    generateConfig();
  }, [generateConfig]);

  // Apply changes
  const handleApply = useCallback(() => {
    generateConfig();
  }, [generateConfig]);

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-md p-4 mb-4 bg-gray-50 dark:bg-gray-800/50">
      <div className="flex items-center gap-2 mb-3">
        <Terminal className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
          Shell Commands
        </h3>
      </div>

      {/* Commands List */}
      <div className="space-y-2 mb-4">
        {commands.map((command, index) => (
          <div
            key={command.id}
            draggable={!disabled}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            className={`flex items-start gap-2 p-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded
              ${draggedIndex === index ? "opacity-50" : ""}
              ${disabled ? "cursor-not-allowed" : "cursor-move"}`}
          >
            {/* Drag handle */}
            <div className="flex-shrink-0 mt-2">
              <GripVertical className="w-4 h-4 text-gray-400" />
            </div>

            {/* Command number */}
            <div className="flex-shrink-0 mt-2 w-6 text-xs text-gray-500 dark:text-gray-400 font-mono">
              {index + 1}.
            </div>

            {/* Command textarea */}
            <textarea
              value={command.content}
              onChange={(e) => handleCommandChange(command.id, e.target.value)}
              onBlur={handleApply}
              disabled={disabled}
              placeholder="Enter command..."
              rows={command.content.split("\n").length || 1}
              className="flex-1 px-2 py-1 text-sm font-mono border-0 bg-transparent
                text-gray-900 dark:text-white
                focus:outline-none focus:ring-2 focus:ring-blue-500 rounded
                disabled:cursor-not-allowed resize-none"
            />

            {/* Delete button */}
            <button
              onClick={() => handleRemoveCommand(command.id)}
              disabled={disabled || commands.length === 1}
              className="flex-shrink-0 mt-1 p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300
                disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete command"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Add Command Button */}
      <button
        onClick={handleAddCommand}
        disabled={disabled}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400
          hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded
          disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Command
      </button>

      {/* Settings */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 space-y-3">
        {/* Timeout */}
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-700 dark:text-gray-300 w-32">
            Timeout (seconds):
          </label>
          <input
            type="number"
            value={timeout}
            onChange={(e) => setTimeoutValue(Number(e.target.value))}
            onBlur={handleApply}
            disabled={disabled}
            min={1}
            max={3600}
            className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded
              bg-white dark:bg-gray-700 text-gray-900 dark:text-white
              focus:outline-none focus:ring-2 focus:ring-blue-500
              disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Continue on Error */}
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={continueOnError}
              onChange={(e) => {
                const newValue = e.target.checked;
                setContinueOnError(newValue);
                // Generate config immediately when checkbox changes
                const config = {
                  commands: commands.map((cmd) => cmd.content),
                  timeout,
                  continue_on_error: newValue,
                };
                onConfigGenerated(config);
              }}
              disabled={disabled}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded
                focus:ring-2 focus:ring-blue-500
                disabled:opacity-50 disabled:cursor-not-allowed"
            />
            Continue on error
          </label>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            (continue executing if a command fails)
          </span>
        </div>
      </div>

      {/* Help text */}
      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <p>• Drag and drop to reorder commands</p>
        <p>• Multi-line commands are supported (use \ for line continuation)</p>
        <p>• Comment lines (starting with #) are shown but not executed</p>
        <p>• Empty lines are automatically skipped</p>
        <p className="font-medium text-blue-600 dark:text-blue-400 mt-2">
          • Use {"{{"} variable {"}"} syntax for dynamic input substitution (e.g., echo {"{{"}message{"}}"})</p>
        <p className="pl-2">Custom inputs are available as both {"{{"} {"}"} variables and $ENV environment variables</p>
      </div>
    </div>
  );
};
